import Vue from 'vue'
import Vuex from 'vuex'
import { unescape } from 'lodash'
import {
  SET_FILTERS,
  SET_FILTERS_ACTIVE,
  SET_FILTERS_PRESELECTED,
  SET_QUERY_STEP,
  SET_PRODUCTS,
  SET_PAGINATION,
  SET_NO_PRODUCTS_BLOCK,
  SET_PARAMS,
  SET_OPTIONS,
  SET_CATEGORY,
  SET_QUERY_STRING,
  SET_CATEGORY_FAQ,
  SET_CATEGORY_DESCR,
  SET_UI,
  SET_FORMKEY,
  SET_PATHNAME,
  SET_REDIRECT_TO_PAGE
} from './mutation-types'
import { setURLFilters, setURLParams } from '../helpers/uri'

Vue.use(Vuex)

/**
 * Validate params passed from URL
 *
 * @param {Object} params Params object
 * @param {Object} state Vuex state
 * @returns validated Params
 */
const validateParams = (params, state) => {
    params.p = params.p && Number(params.p) < 1 ? 1 : Number(params.p)
    params.resultsPP =
        params.resultsPP < 1 ||
        isNaN(params.resultsPP) ||
        params.resultsPP > 100
            ? Number(state.options.pager.default)
            : Number(params.resultsPP)

    if (params.dir && !['desc', 'asc'].includes(params.dir)) {
        params.dir = state.params.dir
    }

    if (
        params.order &&
        !Object.keys(state.options.order.options).find(
            (key) => params.order === key
        )
    ) {
        params.order = state.params.order
    }
    return params
}

const state = {
    // list of available filters
    filters: {},
    // only active filters
    filtersActive: {},
    // preselected filters but not active - usefull for mobile
    filtersPreselected: {},
    // pagination object is returned by API
    pagination: {},
    // category list, returned by API
    category: {},
    // default options passed from Magento
    options: {
        i18n: {
            'Product is unavailable': 'Product is unavailable',
            'No Items Found': 'No Items Found',
            'Check price': 'Check price',
            refunded: 'refunded',
            'not refunded': 'not refunded'
        }
    },
    // searchParams like p, order, dir, resultsPP
    params: {},
    products: [],
    queryStep: 1,
    queryString: '',
    ui: {
        isLoading: false
    },
    formkey: '',
    noProductsBlock: '',
    categoryFaq: '',
    categoryDescr: '',
    pathname: '',
    redirectToPage: '0_page'
}

const actions = {
    async getProducts({ getters, state, commit }) {
        const { getURLParams } = getters

        try {
            commit(SET_UI, { isLoading: true })

            const { data } = await this._vm.$http.get(
                `?${getURLParams.toString()}`
            )

            if (data) {
                const {
                    products,
                    filters,
                    no_products_block,
                    pagination,
                    category,
                    query_step,
                    query_string,
                    faq,
                    category_description,
                    redirect_to_page
                } = data

                if (products) {
                    commit(SET_PRODUCTS, products)
                }

                if (filters) {
                    commit(SET_FILTERS, filters)
                }

                if (no_products_block) {
                    commit(SET_NO_PRODUCTS_BLOCK, no_products_block)
                }

                if (pagination) {
                    commit(SET_PAGINATION, pagination)
                }

                if (category) {
                    commit(SET_CATEGORY, category)
                }

                if (query_step) {
                    commit(SET_QUERY_STEP, query_step)
                }

                if (query_string) {
                    commit(SET_QUERY_STRING, query_string)
                }

                if (faq) {
                    commit(SET_CATEGORY_FAQ, faq)
                }

                if (category_description) {
                    commit(SET_CATEGORY_DESCR, category_description)
                }

                if (redirect_to_page) {
                  commit(SET_REDIRECT_TO_PAGE, redirect_to_page)
                }

                return {
                    products,
                    filters,
                    no_products_block,
                    pagination,
                    category,
                    query_step,
                    category_description,
                    faq,
                    redirect_to_page
                }
            }

            return {
                products: state.products,
                filters: state.filters,
                no_products_block: state.noProductsBlock,
                pagination: state.pagination,
                category: state.category,
                query_step: state.queryStep,
                query_string: state.queryString,
                faq: state.categoryFaq,
                category_description: state.categoryDescr,
                redirect_to_page: redirectToPage
            }
        } catch (err) {
            return err
        } finally {
            commit(SET_UI, { isLoading: false })
        }
    },
    /**
     * Example of usage
     * addFilter({key: 'nameoffilter', values: ['filtervalues'], preselection: !shouldRefreshProducts?})
     */
    addFilter({ state, dispatch }, { key, values, preselection, label }) {
        const filters = preselection
            ? { ...state.filtersPreselected }
            : { ...state.filtersActive }

        const isMultiselect =
            state.filters[key]?.filter_renderer_type === 'multiselect'

        if (filters[key] && Array.isArray(filters[key]) && isMultiselect) {
            filters[key] = [...filters[key], ...values]
        } else {
            filters[key] = values
        }

        window.GoogleAnalyticsUniversal.prototype.activateFilter(
            state.filters[key]?.filter_name,
            label
        )

        dispatch('dispatchFilters', { filters, preselection })
    },
    /**
     * Example of usage
     * removeFilter({key: 'nameoffilter', values: ['filtervalues'], preselection: !sshouldRefreshProducts?})
     */
    removeFilter({ state, dispatch }, { key, values, preselection }) {
        const filters = preselection
            ? { ...state.filtersPreselected }
            : { ...state.filtersActive }

        if (filters[key] && Array.isArray(filters[key])) {
            if (filters[key].length) {
                filters[key] = filters[key].filter(
                    (item) => !values.includes(item)
                )
            }

            if (!filters[key].length) {
                delete filters[key]
            }
        }

        dispatch('dispatchFilters', { filters, preselection })
    },
    /**
     * Example of usage
     * setParams({ p: 2 })
     */
    setParams({ state, commit }, params) {
        const currentParams = {
            ...state.params,
            ...{
                qs: state.queryStep
            }
        }
        const newParams = Object.assign(currentParams, params)
        setURLParams(newParams)
        commit(SET_PARAMS, newParams)
    },
    setPageParams({ state, commit }, params) {
        const currentParams = {
            ...state.params,
            ...{
                qs: state.queryStep
            }
        }

        const newParams = Object.assign(currentParams, params)
        const pageParams = Object.assign(params)

        setURLParams(pageParams)
        commit(SET_PARAMS, newParams)
    },
    cleanFilters({ dispatch }, { preselection = false }) {
        dispatch('dispatchFilters', { filters: {}, preselection })
    },
    dispatchPreselectedFilters({ state, dispatch }) {
        dispatch('dispatchFilters', { filters: state.filtersPreselected })
    },
    /**
     * Get filters, set page to 1 and refresh products
     */
    dispatchFilters(
        { dispatch, commit, state },
        { filters = state.filtersActive, preselection = false }
    ) {
        if (!preselection) {
            commit(SET_FILTERS_ACTIVE, filters)
            commit(SET_FILTERS_PRESELECTED, {})

            dispatch('setParams', {
                p: 1
            })
            setURLFilters(filters)

            dispatch('getProducts')
        } else {
            commit(SET_FILTERS_PRESELECTED, filters)
        }
    }
}

const getters = {
    /**
     * Get active filters list only
     */
    getFiltersActive(state) {
        const filters = {}
        const activeFilters = { ...state.filtersActive }

        const getActiveForMultiSelect = (currentFilter, activeValues) => {
            const activeFilters = []
            activeValues.forEach((value) => {
                const filter = currentFilter.values.find((filterValue) => {
                    return String(filterValue.value) === String(value)
                })

                if (filter) {
                    activeFilters.push({
                        ...filter,
                        filterName: currentFilter.filter_name
                    })
                }
            })
            return activeFilters
        }

        const getActiveForSingleSelect = (currentFilter, activeValues) => [
            {
                ...currentFilter,
                ...{ value: activeValues[0] }
            }
        ]

        try {
            for (const [activeFilterKey, activeFilterValues] of Object.entries(
                activeFilters
            )) {
                const currentFilter = state.filters[activeFilterKey]
                if (
                    currentFilter &&
                    activeFilterValues &&
                    Array.isArray(activeFilterValues)
                ) {
                    filters[activeFilterKey] = currentFilter.values
                        ? getActiveForMultiSelect(
                              currentFilter,
                              activeFilterValues
                          )
                        : getActiveForSingleSelect(
                              currentFilter,
                              activeFilterValues
                          )
                }
            }
        } catch (err) {
            console.log(err)
        }
        return filters
    },
    /**
     * Find filter by key with merged active state
     * @param {String} key
     * @returns Filter object
     */
    getFilterByKey: (state) => (key, preselection) => {
        if (typeof state.filters[key] === 'undefined') {
            return null
        }

        const activeFilters = preselection
            ? { ...state.filtersPreselected }
            : { ...state.filtersActive }

        let filter = { ...state.filters[key] }
        const isMultiselect = filter.filter_renderer_type === 'multiselect'

        if (filter.values && isMultiselect) {
            filter.values = filter.values.map(({ ...filter }) => {
                filter.active = !!activeFilters[key]?.find(
                    (activeFilter) => activeFilter === filter.value
                )

                return filter
            })
        } else {
            filter.active = Array.isArray(activeFilters[key])
                ? activeFilters[key][0]
                : activeFilters[key]

            if (filter.active && filter.values) {
                const active = filter.values.find(
                    ({ value }) => value === filter.active
                )
                filter.values = filter.values.filter(
                    ({ value }) => value !== active.value
                )
                filter.values.unshift(active)
            }
        }

        return filter
    },
    sortedCategoryList: ({ category }) => {
        if (!category.children || !Object.keys(category.children).length) {
            return category
        }

        const sortedList = Object.values(category.children)
            .map((category) => category)
            .sort((a, b) => a.name.localeCompare(b.name))

        return {
            ...category,
            children: sortedList
        }
    },
    /**
     * Return searchParams object from current params and filters
     * Usefull for replacing url or passing whole GET to API
     */
    getURLParams: ({ params, filtersActive }) =>
        new URLSearchParams({
            ...params,
            ...filtersActive
        }),
    maxPriceInfo: ({ options }) => unescape(options?.category?.maxPriceInfo),
    maxPriceInfoTooltip: ({ options }) =>
        unescape(options?.category?.maxPriceInfoTooltip),
    i18n: ({ options }) => (key) => options?.i18n?.[key] || key,
    isSearchPage: ({ options, params }) =>
        Boolean(!options?.category?.isCategory && params.q),
    isMobile: () => Boolean(window.innerWidth < 767)
}

const mutations = {
    [SET_FILTERS_ACTIVE](state, filters) {
        state.filtersActive = filters
    },
    [SET_FILTERS_PRESELECTED](state, filters) {
        state.filtersPreselected = filters
    },
    [SET_FILTERS](state, filters) {
        state.filters = Object.assign(
            { ...state.options?.staticFilters } || {},
            filters
        )
    },
    [SET_PAGINATION](state, pagination) {
        state.pagination = pagination
    },
    [SET_NO_PRODUCTS_BLOCK](state, no_products_block) {
        state.noProductsBlock = no_products_block
    },
    [SET_PRODUCTS](state, products) {
        state.products = products
    },
    [SET_PARAMS](state, params) {
        state.params = validateParams(params, state)
    },
    [SET_OPTIONS](state, options) {
        state.options = Object.freeze(options)
    },
    [SET_UI](state, payload) {
        state.ui = Object.assign(state.ui, payload)
    },
    [SET_CATEGORY](state, payload) {
        if (!payload) return
        state.category = payload
    },
    [SET_QUERY_STEP](state, payload) {
        state.queryStep = payload
    },
    [SET_QUERY_STRING](state, payload) {
        state.queryString = payload
    },
    [SET_FORMKEY](state, payload) {
        state.formkey = payload
    },
    [SET_CATEGORY_FAQ](state, faq) {
        state.categoryFaq = faq
    },
    [SET_CATEGORY_DESCR](state, category_description) {
        state.categoryDescr = category_description
    },
    [SET_PATHNAME](state, payload) {
        state.pathname = payload
    },
    [SET_REDIRECT_TO_PAGE](state, payload) {
      state.redirectToPage = payload
    }
}

const store = new Vuex.Store({
    state,
    getters,
    actions,
    mutations
})

export default store
